import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { usePersonSequenceEnrollments } from "@/hooks/usePerson";

export function PersonSequencesTab({
  leadId,
}: {
  leadId?: string;
}) {
  const { data, isLoading } = usePersonSequenceEnrollments(leadId);

  if (!leadId) {
    return (
      <Empty
        title="Save this person first"
        body="Sequence enrollments require a saved contact in Leads."
      />
    );
  }

  if (isLoading) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Loading sequences…
      </p>
    );
  }

  const enrollments = data?.enrollments ?? [];
  if (enrollments.length === 0) {
    return (
      <Empty
        title="Not in any sequence"
        body="Enroll this contact from Sequences or the people list actions."
        action={
          <Link to="/sequences" className="text-sm text-brand hover:underline">
            Browse sequences
          </Link>
        }
      />
    );
  }

  return (
    <ul className="space-y-2">
      {enrollments.map((e) => (
        <li
          key={e.enrollmentId}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2.5"
        >
          <div>
            <Link
              to={`/sequences/${e.sequenceId}`}
              className="text-sm font-medium text-brand hover:underline"
            >
              {e.sequenceName ?? e.sequenceId}
            </Link>
            <p className="text-xs text-muted-foreground">
              Status: {e.status}
              {e.enrolledAt
                ? ` · Enrolled ${new Date(e.enrolledAt).toLocaleDateString()}`
                : ""}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}
